import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CircleCheck } from "lucide-react";

import Button from "../../../../../shared/ui/button/Button";
import { sessionService } from "../../../application/SessionApplicationService";
import type { Session } from "../../../domain/Session";

interface Props {
    sessionId: string;
    onChange: (changes: Pick<Session, "session_status">) => void;
}

/**
 * Asked of a past session with notes that is still pending: did it take
 * place? Notes alone never say so — they may have been written ahead — so
 * the therapist answers, with one tap.
 */
export default function SessionHeldPrompt({ sessionId, onChange }: Props) {
    const { t } = useTranslation();
    const [saving, setSaving] = useState(false);
    const [failed, setFailed] = useState(false);

    const markFinished = async () => {
        setSaving(true);
        setFailed(false);
        try {
            await sessionService.updateSession(sessionId, { session_status: "finished" });
            onChange({ session_status: "finished" });
        } catch {
            setFailed(true);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            role="status"
            className="flex flex-col gap-3 rounded-2xl border border-brand-200 bg-brand-50/60 p-5 dark:border-brand-500/30 dark:bg-brand-500/10 sm:flex-row sm:items-center sm:justify-between"
        >
            <div className="flex items-start gap-3">
                <CircleCheck className="mt-0.5 h-5 w-5 flex-shrink-0 text-brand-500" aria-hidden="true" />
                <div>
                    <p className="text-sm font-semibold text-content-primary">{t("session.heldPrompt.title")}</p>
                    <p className="text-sm leading-relaxed text-content-secondary">{t("session.heldPrompt.description")}</p>
                    {failed && (
                        <p role="alert" className="mt-1 text-sm text-red-600 dark:text-red-400">
                            {t("session.statusSaveFailed")}
                        </p>
                    )}
                </div>
            </div>
            <Button size="sm" onClick={() => void markFinished()} disabled={saving} loading={saving}>
                {t("session.heldPrompt.markFinished")}
            </Button>
        </div>
    );
}
